// Lib
import { FC, Fragment, useEffect, useState } from "react";
// Api
import { useGetLoyaltyQuery } from "rtkQuery/query/loyaltyAPI";
// Hooks
import { useNotification, useViewport } from "hooks";
// Types
import { Loyalty } from "types/loyalty";
import { ModifiedPerkItem } from "types/healthyPerk";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { LoyaltyIcon } from "icons";
// Components
import { Modal } from "components";
import { Checkbox } from "components/Form";
// Styled
import { FlexContainer } from "styled/Box";
import { Button } from "styled/Buttons";
import { Label } from "components/Form/Switch/styled";

import { Description, IconContainer, StyledGrid, Title } from "./styled";

interface SelectPerkTierModalProps {
  perk: ModifiedPerkItem | false;
  isDisabled?: boolean;
  onClose: () => void;
  onSelect: (perk: ModifiedPerkItem, tiers: Loyalty[]) => void;
}

export const SelectPerkTierModal: FC<SelectPerkTierModalProps> = ({
  perk,
  isDisabled,
  onClose,
  onSelect,
}) => {
  const { isMobile } = useViewport();

  const { openNotification } = useNotification();

  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);

  useEffect(() => {
    if (!perk) {
      setSelectedTiers([]);
      return;
    }

    setSelectedTiers(() => {
      if (perk.tiersData?.length) {
        return perk.tiersData.map(tier => tier.id);
      }
      return [];
    });
  }, [perk]);

  const { data, isLoading, error } = useGetLoyaltyQuery(null);

  useEffect(() => {
    if (error) {
      errorHandler({ error, openNotification });
    }
  }, [error]);

  const handleClose = () => {
    if (isDisabled) return;

    setSelectedTiers([]);
    onClose();
  };

  const handleSelectTiers = () => {
    if (!perk) return;

    if (selectedTiers.length) {
      const selectedTierData = data?.filter(tier =>
        selectedTiers.includes(tier.id),
      );
      if (selectedTierData) {
        onSelect(perk, selectedTierData);
      }
    }
  };

  return (
    <Modal
      destroyOnClose
      closeIcon={false}
      open={!!perk}
      width={480}
      onClose={handleClose}
    >
      <FlexContainer
        $fullwidth
        $column
        $gap={8}
        $align="center"
        $justify="center"
        $padding="24px 0 0"
      >
        <IconContainer>
          <LoyaltyIcon />
        </IconContainer>

        <Title>Loyalty tier</Title>
        <Description>Select Loyalty tiers related to </Description>
        {perk && <Title>&#34;{perk?.product?.name}&#34;</Title>}

        <StyledGrid>
          {data?.map(tier => (
            <Fragment key={tier.id}>
              <Checkbox
                label={tier.name}
                style={{ paddingLeft: isMobile ? 0 : "40px" }}
                name="tier"
                disabled={isLoading}
                checked={selectedTiers.includes(tier.id)}
                onChange={e => {
                  if (e.target.checked) {
                    setSelectedTiers(prev => [...prev, tier.id]);
                  }

                  if (!e.target.checked) {
                    setSelectedTiers(prev =>
                      prev.filter(selectedTier => selectedTier !== tier.id),
                    );
                  }
                }}
              />

              <Label>Goal level Points: {tier?.goalLevelPoints}</Label>
            </Fragment>
          ))}
        </StyledGrid>

        <FlexContainer
          $fullwidth
          $align="center"
          $justify="flex-end"
          $gap={8}
          $padding="24px 0 0"
          $column={isMobile}
        >
          <Button.Heading
            $fullWidth={isMobile}
            disabled={isLoading || isDisabled}
            onClick={handleClose}
          >
            Cancel
          </Button.Heading>

          <Button.Heading
            $fullWidth={isMobile}
            type="primary"
            loading={isLoading || isDisabled}
            disabled={!selectedTiers.length}
            onClick={handleSelectTiers}
          >
            Save
          </Button.Heading>
        </FlexContainer>
      </FlexContainer>
    </Modal>
  );
};
